import PropTypes from 'prop-types';
import React, { memo, useCallback, useEffect, useRef } from 'react';
import classNames from 'classnames';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import { CompanyBuyerProjects } from '../../../../components/Company/CompanyBuyer/CompanyBuyerProjects';
import { buildSortByParam } from '../../../../helpers/paramBuilder';

import {
  getBuyerProjectsPlatform,
  getBuyerProjectsAddon,
  updateProjects,
  createPlatformProject,
} from '../../../../actions/company/buyer/projects';

import { fetchTargetStatuses } from '../../../../actions/statuses';
import { usePopupContext } from '../../../../contexts/PopupContext';

import style from './style.module.css';

const { func, object, bool } = PropTypes;

const CompanyBuyerProjectsContainer = ({
  projects,
  isDuplicateCompany,
  match,
  getBuyerProjectsPlatform,
  getBuyerProjectsAddon,
  updateProjects,
  createPlatformProject,
  fetchTargetStatuses,
}) => {
  const { openPopup } = usePopupContext();
  const prevPlatformRef = useRef(null);
  const prevAddonRef = useRef(null);

  const { companyId } = match.params;

  const getCanEditData = useCallback(() => !isDuplicateCompany, [isDuplicateCompany]);

  const handleGetNextPageDataForPlatform = useCallback(
    page => {
      const platform = projects.get('platform');
      const sortBy = buildSortByParam(platform);

      getBuyerProjectsPlatform({ companyId, page, sortBy });
    },
    [getBuyerProjectsPlatform, companyId, projects],
  );

  const handleGetNextPageDataForAddon = useCallback(
    page => {
      const addon = projects.get('addon');
      const sortBy = buildSortByParam(addon);

      getBuyerProjectsAddon({ companyId, page, sortBy });
    },
    [getBuyerProjectsAddon, companyId, projects],
  );

  const handleDoubleClickRow = useCallback(
    row => {
      if (!getCanEditData()) return;

      window.open(`/project/${row.data.id}`, '_blank');
    },
    [getCanEditData],
  );

  const handleCurrentStatusChange = useCallback(
    event => {
      const { value } = event.target;

      updateProjects({
        filterName: 'status',
        filterData: {
          selected: {
            name: value,
            value,
          },
        },
      });
    },
    [updateProjects],
  );

  const handlePlatformChange = useCallback(
    data => {
      updateProjects({
        filterName: 'platform',
        filterData: {
          [data.filterName]: data.filterData,
        },
      });
    },
    [updateProjects],
  );

  const handleAddonChange = useCallback(
    data => {
      updateProjects({
        filterName: 'addon',
        filterData: {
          [data.filterName]: data.filterData,
        },
      });
    },
    [updateProjects],
  );

  const handleCreateNewPlatformProject = useCallback(
    (formData, callback) => {
      const body = {
        type: 'Platform',
        active: true,
        category: formData.get('category'),
      };

      if (formData.getIn(['harvcoLead', 'selected'])) {
        body.harvco_lead_id = formData.getIn(['harvcoLead', 'selected', 'id']);
      }

      if (formData.getIn(['clientLead', 'selected'])) {
        body.client_lead_id = formData.getIn(['clientLead', 'selected', 'id']);
      }

      if (formData.getIn(['executive', 'selected'])) {
        body.exec_id = formData.getIn(['executive', 'selected', 'id']);
      }
      body.buyerId = match.params.companyId;
      createPlatformProject({ body, callback });
    },
    [match, createPlatformProject],
  );

  const handleAddPlatformProject = useCallback(() => {
    if (!getCanEditData()) return;

    openPopup('AddNewPlatformProject', {
      buyerId: companyId,
      onCreate: handleCreateNewPlatformProject,
      onSuccessResponse: () => {
        handleGetNextPageDataForPlatform(1);
      },
    });
  }, [getCanEditData, companyId, openPopup, handleCreateNewPlatformProject, handleGetNextPageDataForPlatform]);

  const handleGenerateSummaryReport = useCallback(() => {
    if (!getCanEditData()) return;

    fetchTargetStatuses(() => {
      openPopup('ClientSummaryReportPopup', {
        buyerId: companyId,
      });
    });
  }, [getCanEditData, fetchTargetStatuses, openPopup, companyId]);

  useEffect(() => {
    getBuyerProjectsPlatform({ companyId });
    getBuyerProjectsAddon({ companyId });
  }, []);

  useEffect(() => {
    const platform = projects.get('platform');
    const addon = projects.get('addon');
    const prevPlatform = prevPlatformRef.current;
    const prevAddon = prevAddonRef.current;

    prevPlatformRef.current = platform;
    prevAddonRef.current = addon;

    if (prevPlatform !== null && prevPlatform !== platform) {
      const oldSortBy = buildSortByParam(prevPlatform);
      const sortBy = buildSortByParam(platform);

      if (sortBy !== oldSortBy) {
        getBuyerProjectsPlatform({ companyId, page: 1, sortBy });
      }
    }

    if (prevAddon !== null && prevAddon !== addon) {
      const oldSortBy = buildSortByParam(prevAddon);
      const sortBy = buildSortByParam(addon);

      if (sortBy !== oldSortBy) {
        getBuyerProjectsAddon({ companyId, page: 1, sortBy });
      }
    }
  }, [projects]);

  return (
    <div className={classNames(style.host, 'tab-pane active')} id="slide-3">
      <div className="col-xs-12 col-sm-12 col-md-12 col-lg-12 p0 full-height">
        <CompanyBuyerProjects
          canEditData={getCanEditData()}
          onAddonChange={handleAddonChange}
          onAddPlatformProject={handleAddPlatformProject}
          onCurrentStatusChange={handleCurrentStatusChange}
          onDoubleClickRow={handleDoubleClickRow}
          onGenerateReport={handleGenerateSummaryReport}
          onGetNextPageDataForAddon={handleGetNextPageDataForAddon}
          onGetNextPageDataForPlatform={handleGetNextPageDataForPlatform}
          onPlatformChange={handlePlatformChange}
          projects={projects}
        />
      </div>
    </div>
  );
};

CompanyBuyerProjectsContainer.propTypes = {
  createPlatformProject: func.isRequired,
  getBuyerProjectsAddon: func.isRequired,
  getBuyerProjectsPlatform: func.isRequired,
  isDuplicateCompany: bool.isRequired,
  projects: object.isRequired,
  updateProjects: func.isRequired,
};
export { CompanyBuyerProjectsContainer };
export default connect(
  state => ({
    projects: state.targetCompany.buyer.projects,
    isDuplicateCompany: state.targetCompany.mergeInfo.get('isDuplicateCompany'),
  }),
  dispatch =>
    bindActionCreators(
      {
        getBuyerProjectsPlatform,
        getBuyerProjectsAddon,
        updateProjects,
        createPlatformProject,
        fetchTargetStatuses,
      },
      dispatch,
    ),
)(memo(CompanyBuyerProjectsContainer));
